import { ControllerFlowAPI } from '@wix/yoshi-flow-editor';
import { request } from '../services/request';
import { querySteps } from '@wix/ambassador-online-programs-v3-step/http';
import type { Step } from '../types/v3Types';

const STEPS_CHUNK_SIZE = 100; // Number of steps to load per request

interface LoadStepsOptions {
  programId: string;
  flowAPI: ControllerFlowAPI;
}

/**
 * Loads all steps for a program in chunks using cursor pagination
 * @param options - Configuration for loading steps
 * @returns Promise with array of steps
 */
export async function loadStepsInChunks(
  options: LoadStepsOptions,
): Promise<Step[]> {
  const { programId, flowAPI } = options;
  const steps: Step[] = [];
  let cursor: string | undefined;
  let hasMore = true;

  // Load steps in chunks using cursor pagination
  while (hasMore) {
    const currentCursor = cursor;
    const response = await request({
      flowAPI,
      options: querySteps({
        programId,
        query: currentCursor
          ? {
              // When cursor exists, pass only cursor
              cursorPaging: {
                cursor: currentCursor,
                limit: STEPS_CHUNK_SIZE,
              },
            }
          : {
              // First request: include filter
              filter: {
                programId,
              },
              cursorPaging: {
                limit: STEPS_CHUNK_SIZE,
              },
            },
      }),
      errorOptions: {
        errorCodesMap: {
          applicationError: {},
        },
      },
    });

    // Add loaded steps to the result
    if (response?.data?.steps) {
      steps.push(...response.data.steps);
    }

    // Check if there are more pages to load
    hasMore = response?.data?.pagingMetadata?.hasNext || false;
    cursor = response?.data?.pagingMetadata?.cursors?.next;

    // Log progress for debugging
    if (hasMore) {
      console.log(
        `[OOI] Loaded ${steps.length} steps so far, loading next chunk...`,
      );
    }
  }

  console.log(`[OOI] Finished loading all ${steps.length} steps`);
  return steps;
}
